import { useQuery } from "@tanstack/react-query";
import { AdminTopline, Panel } from "../../components/console";
import { api, authHeaders, describeError } from "../../lib/api";
import type { AuditEvent, StaffMember } from "../../lib/api";
import { getCurrentRole } from "../../lib/auth";
import { ROLE_LABEL_LO } from "../../lib/roles";
import { shortId } from "../../lib/format";

/**
 * ป้ายภาษาลาวของแต่ละชนิดเหตุการณ์ — ต้องครบทุกค่าที่ `domain.AuditAction` มี
 * ค่าที่ไม่รู้จักจะถูกแสดงเป็นรหัสดิบ ซึ่งบอกว่ามีชนิดใหม่ที่ยังไม่ได้แปล
 */
const ACTION_LABEL_LO: Record<string, string> = {
  "request.signed": "ເຊັນຄຳຮ້ອງ",
  "request.issued": "ອອກເອກະສານ",
  "request.approved": "ອະນຸມັດຄຳຮ້ອງ",
  "request.rejected": "ປະຕິເສດຄຳຮ້ອງ",
  "fee_proposal.created": "ສະເໜີແກ້ໄຂອັດຕາ",
  "fee_proposal.approved": "ອະນຸມັດການສະເໜີ",
  "fee_proposal.rejected": "ປະຕິເສດການສະເໜີ",
  "user.created": "ເປີດບັນຊີ",
  "user.updated": "ແກ້ໄຂບັນຊີ",
  "user.deactivated": "ປິດບັນຊີ",
  "document_type.saved": "ແກ້ໄຂແຄັດຕາລັອກ",
  "document_template.saved": "ອັບໂຫຼດແບບຟອມ PDF",
  "document_template.removed": "ລຶບແບບຟອມ PDF",
  "document_template.fields_saved": "ວາງຊ່ອງໃນແບບຟອມ PDF",
  "document_layout.saved": "ບັນທຶກຮ່າງແມ່ແບບເອກະສານ (AI)",
  "document_layout.activated": "ເປີດໃຊ້ແມ່ແບບເອກະສານ",
  "document_layout.disabled": "ປິດໃຊ້ແມ່ແບບເອກະສານ",
  "province.created": "ເພີ່ມແຂວງ",
  "province.updated": "ແກ້ໄຂແຂວງ",
  "province.deleted": "ລຶບແຂວງ",
  "district.created": "ເພີ່ມເມືອງ",
  "district.updated": "ແກ້ໄຂເມືອງ",
  "district.deleted": "ລຶບເມືອງ",
  "village.created": "ເພີ່ມບ້ານ",
  "village.updated": "ແກ້ໄຂບ້ານ",
  "village.deleted": "ລຶບບ້ານ",
  "tasaeng.created": "ເພີ່ມຕາແສງ",
  "tasaeng.updated": "ແກ້ໄຂຕາແສງ",
  "tasaeng.deleted": "ລຶບຕາແສງ",
  "tasaeng.villages_set": "ປ່ຽນບ້ານໃນຕາແສງ",
  // break-glass (ADR-0032) — รายละเอียดดูได้ที่จอ ຄຳຂໍເປີດເບິ່ງຂໍ້ມູນ › ໃຜເບິ່ງຂໍ້ມູນປະຊາຊົນ
  "pii_access.requested": "ຂໍເປີດເບິ່ງຂໍ້ມູນເຕັມ",
  "pii_access.approved": "ອະນຸມັດການເປີດເບິ່ງຂໍ້ມູນ",
  "pii_access.rejected": "ບໍ່ອະນຸມັດການເປີດເບິ່ງຂໍ້ມູນ",
  "pii_access.revoked": "ຍົກເລີກ/ຖອນສິດເປີດເບິ່ງຂໍ້ມູນ",
  "request.pii_viewed": "ເປີດເບິ່ງຂໍ້ມູນເຕັມ (break-glass)",
};

/** สีของ badge ตามน้ำหนักของเหตุการณ์ ไม่ใช่ตามลำดับ */
function actionBadgeClass(action: string): string {
  if (action.endsWith(".rejected") || action.endsWith(".deactivated") || action.endsWith(".deleted")) {
    return "status-badge status-badge--danger";
  }
  if (action.endsWith(".approved") || action.endsWith(".signed") || action.endsWith(".issued")) {
    return "status-badge status-badge--success";
  }
  return "status-badge status-badge--info";
}

async function fetchEvents(): Promise<AuditEvent[]> {
  const { data, error, response } = await api.GET("/v1/admin/audit-events", {
    params: { query: { limit: 100 } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return data.items;
}

async function fetchStaff(): Promise<Map<string, StaffMember>> {
  const { data, error, response } = await api.GET("/v1/admin/staff", {
    params: { query: { limit: 200 } },
    headers: authHeaders(),
  });
  if (error || !data) throw new Error(describeError(error, response.status));
  return new Map(data.items.map((u) => [u.id, u] as const));
}

/** เวลาแบบ `27/09 14:02` ตาม mockup */
function stamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function DistrictAuditPage() {
  const role = getCurrentRole();
  const events = useQuery({ queryKey: ["audit-events"], queryFn: fetchEvents });
  const staff = useQuery({ queryKey: ["staff", "map"], queryFn: fetchStaff });

  const rows = events.data ?? [];

  return (
    <div>
      <AdminTopline
        title="ບັນທຶກການເຄື່ອນໄຫວ"
        subtitle="ບັນທຶກນີ້ເພີ່ມໄດ້ຢ່າງດຽວ ແກ້ ຫຼື ລຶບບໍ່ໄດ້"
        roleLabel={role ? ROLE_LABEL_LO[role] : undefined}
      />

      {events.error ? (
        <div className="alert-error" style={{ marginBottom: "var(--space-4)" }}>
          ໂຫຼດຂໍ້ມູນບໍ່ສຳເລັດ: {events.error.message}
        </div>
      ) : null}

      <Panel title="ເຫດການຫຼ້າສຸດ">
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>ເວລາ</th>
                <th>ຜູ້ກະທຳ</th>
                <th>ການກະທຳ</th>
                <th>ອ້າງອີງ</th>
              </tr>
            </thead>
            <tbody>
              {events.isPending ? (
                <tr>
                  <td colSpan={4} className="data-table__empty">
                    ກຳລັງໂຫຼດ…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="data-table__empty">
                    ຍັງບໍ່ມີການເຄື່ອນໄຫວໃນເຂດນີ້
                  </td>
                </tr>
              ) : (
                rows.map((e) => (
                  <tr key={e.id}>
                    <td className="num">{stamp(e.occurred_at)}</td>
                    <td>
                      {/* ชื่อถูก join ตอนอ่าน ส่วน summary เก็บชื่อ ณ ตอนเกิดเหตุไว้แล้ว
                          บัญชีที่ถูกปิดไปจึงยังมีชื่อโผล่ในคอลัมน์ ການກະທຳ */}
                      {staff.data?.get(e.actor_id)?.full_name ?? shortId(e.actor_id)}
                      <div className="caption">{ROLE_LABEL_LO[e.actor_role]}</div>
                    </td>
                    <td>
                      <div className="row" style={{ gap: "var(--space-2)", flexWrap: "wrap" }}>
                        <span className={actionBadgeClass(e.action)}>
                          {ACTION_LABEL_LO[e.action] ?? e.action}
                        </span>
                        <span className="body-sm">{e.summary}</span>
                      </div>
                    </td>
                    <td className="num">
                      {e.subject_id ? `#${shortId(e.subject_id)}` : "—"}
                      {e.ip ? <div className="caption num">{e.ip}</div> : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
